import { BalanceData, GearTarget, Rarity, upgradeCostMult } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { GearBonus, GearItem, GearItemJson, GearOwner, SalvageYield, bonusFor, gearFromJson, gearScore, gearToJson, generateGear, salvageYield, sameOwner, seatedFit } from "./gear";
import { ForgeJson, ForgeStock } from "./forgeStock";
import { QueuedUpgrade, cancelQueued, isQueued, parseQueued, startQueued } from "./upgradeQueue";
import { NO_VIP_BENEFITS, VipBenefits } from "../data/vip";
import { vipBuildSeconds, withVipCombat } from "./vipLevels";
import { ResearchBook, withResearch } from "./research";
import { goldFromLegacy, isLegacyResources } from "./gold";
import { SupplyStock } from "./supplies";
import { PetsState } from "./pets";

/** Seat-targeted chest gear (§9.3 early gear): roll for the free seats of this level's readiness plan; `owned` = tower types owned now. */
export interface ChestSeats { readonly level: number; readonly owned: readonly boolean[] }

/** What a persistent upgrade raises: a tower type (`towerIndex`), the Fortress, or a pet (Pets v2: `towerIndex` is its pets.json roster index). */
export const enum UpgradeTarget { Tower = 0, Base = 1, Pet = 2 }

/** A running persistent upgrade (§8): finishes at endAt (Unix seconds, server clock later). */
export interface UpgradeJob {
  readonly id: number;
  readonly target: UpgradeTarget;
  /** Tower archetype index for towers; ignored for the base. */
  readonly towerIndex: number;
  readonly toLevel: number;
  readonly startedAt: number;
  endAt: number;
  readonly durationSeconds: number;
}

/**
 * Persistent player state (DESIGN §6.3, §7, §8): tower and base levels 1–255, gold, builders and
 * running upgrade jobs. Pure rules; the wall clock is passed in.
 */
export class PlayerProfile {
  readonly towerLevels: number[];
  baseLevel = 1;
  /** The one resource (meta/gold.ts): chests, Orders, Tribute… pay it; tower and fortress upgrades spend it. */
  gold = 0;
  buildersOwned = 1;
  readonly jobs: UpgradeJob[] = [];
  /** Paid next upgrades waiting for a builder or their target (D4, meta/upgradeQueue.ts). */
  readonly queue: QueuedUpgrade[] = [];
  private nextJobId = 1;
  /** Timer reductions applied today (UTC day number) for the daily cap (§8.3). */
  reductionsToday = 0;
  reductionDay = -1;
  /** Speed-up inventory (§9.3): minutes per item, each applied whole to one job. */
  readonly speedUps: number[] = [];
  /** Gear inventory (§9.4), equipped items included (`equippedTo` set). */
  readonly gear: GearItem[] = [];
  /** Forge Essence, Fusion Sigils and the fusion tally (gear v2, docs/GEAR_V2.md; meta/forge.ts spends them). */
  readonly forge = new ForgeStock();
  private nextGearId = 1;
  /**
   * VIP benefits (docs/SHOP.md §8, meta/vipLevels.ts), set by @highmarch/app Deals.syncPerks from the `vip` save section;
   * derived, so not saved. Its combat reaches a level only through `gearBonuses()` (the per-archetype GearBonus the sim
   * is given), its build speed through `durationFor` (new jobs only: a running job keeps its end time); the reductions
   * cap, the queue and the free finish (meta/upgradeQueue.ts) read its QoL numbers. All zero at VIP 0.
   */
  vip: VipBenefits = NO_VIP_BENEFITS;
  /**
   * Gold Research owned (meta/research.ts, docs/RESEARCH.md): loaded from and saved to the `research` save section by
   * @highmarch/app (LevelCatalog), not part of `toJson`. Reaches a level only through `gearBonuses()`.
   */
  readonly research = new ResearchBook();
  /** Pre-battle supplies held and armed (MP-06, meta/supplies.ts): the `consumables` save section, like `research`. */
  readonly supplies = new SupplyStock();
  /** Pets v2 (meta/pets.ts): the pets caught and their levels, the pet equipped, the treats: the `pets` save section, like `supplies`. */
  readonly pets: PetsState;

  constructor(readonly balance: BalanceData) {
    this.towerLevels = balance.towers.map(() => 1);
    this.pets = new PetsState(balance.pets, balance.tables.maxLevel);
  }

  levelOf(target: UpgradeTarget, towerIndex: number): number {
    return target === UpgradeTarget.Base ? this.baseLevel : target === UpgradeTarget.Pet ? this.pets.levelAt(towerIndex) : this.towerLevels[towerIndex];
  }

  /** Pets v2: a pet's level never passes the highest tower level (towers, not the Fortress), nor the towers' max. */
  get petLevelCap(): number {
    let top = 1;
    this.balance.towers.forEach((t, i) => { if (!t.isBase) top = Math.max(top, this.towerLevels[i]); });
    return Math.min(this.balance.tables.maxLevel, top);
  }

  /** The highest level a target may be upgraded to now: the towers' max, a pet's cap (0 for a pet not owned). */
  upgradeCap(target: UpgradeTarget, towerIndex: number): number {
    if (target !== UpgradeTarget.Pet) return this.balance.tables.maxLevel;
    return this.pets.levelAt(towerIndex) > 0 ? this.petLevelCap : 0;
  }

  /** Gold to upgrade to `toLevel` (§6.3): meta.json upgradeGoldBase scaled by the cost curve (curves.json
   *  `upgradeCostGrowth`; M38: no longer the damage curve) and, from level 10, the free wall's cost curve (docs/SHOP.md
   *  §3.3 K1, `upgradeCostMult`). */
  costFor(toLevel: number): number {
    const m = this.balance.tables.upgradeCostAt(toLevel).mul(upgradeCostMult(this.balance.meta, toLevel));
    return Fix64.fromInt(this.balance.meta.upgradeGoldBase).mul(m).floorToInt();
  }

  /** An upgrade's timer (§8), shortened by VIP build speed (unchanged at VIP 0). */
  durationFor(toLevel: number): number { return vipBuildSeconds(this.balance.tables.timerSecondsAt(toLevel), this.vip.buildSpeedPercent); }

  canAfford(cost: number): boolean { return this.gold >= cost; }

  isUpgrading(target: UpgradeTarget, towerIndex: number): boolean {
    return this.jobs.some((j) => j.target === target && (target === UpgradeTarget.Base || j.towerIndex === towerIndex));
  }

  get freeBuilders(): number { return this.buildersOwned - this.jobs.length; }

  /** Starts an upgrade: needs gold, a free builder and no running job on the same target. */
  startUpgrade(target: UpgradeTarget, towerIndex: number, now: number): UpgradeJob | string {
    const level = this.levelOf(target, towerIndex);
    if (level >= this.balance.tables.maxLevel) return "max level";
    if (target === UpgradeTarget.Pet && level === 0) return "no such pet";
    if (level >= this.upgradeCap(target, towerIndex)) return "level cap";
    if (this.isUpgrading(target, towerIndex)) return "already upgrading";
    if (isQueued(this, target, towerIndex)) return "already queued";
    if (this.freeBuilders <= 0) return "no free builder";
    const cost = this.costFor(level + 1);
    if (!this.canAfford(cost)) return "not enough gold";
    this.gold -= cost;
    const duration = this.durationFor(level + 1);
    const job: UpgradeJob = { id: this.allocateJobId(), target, towerIndex, toLevel: level + 1, startedAt: now, endAt: now + duration, durationSeconds: duration };
    this.jobs.push(job);
    return job;
  }

  /** A fresh id for a job or a queued upgrade (a queued one keeps its id when it starts). */
  allocateJobId(): number { return this.nextJobId++; }

  /**
   * Applies every job whose end time has passed, earliest first, and starts queued upgrades as builders free up (D4):
   * at the finished job's end time, so a queue runs on while the game is closed and its next job may finish too.
   * Anything still queued that can start now (a builder bought or freed by a cancel) starts at `now`. Returns the
   * completed jobs in the order they finished.
   */
  claimCompleted(now: number): UpgradeJob[] {
    const done: UpgradeJob[] = [];
    for (;;) {
      let next = -1;
      this.jobs.forEach((j, i) => { if (j.endAt <= now && (next < 0 || j.endAt < this.jobs[next].endAt)) next = i; });
      if (next < 0) break;
      const [j] = this.jobs.splice(next, 1);
      if (j.target === UpgradeTarget.Base) this.baseLevel = Math.max(this.baseLevel, j.toLevel);
      else if (j.target === UpgradeTarget.Pet) this.pets.raiseLevelAt(j.towerIndex, j.toLevel);
      else this.towerLevels[j.towerIndex] = Math.max(this.towerLevels[j.towerIndex], j.toLevel);
      done.push(j);
      startQueued(this, j.endAt);
    }
    startQueued(this, now);
    return done;
  }

  /** Play reduces timers (§8.3): 30 s × level cleared off every running job, half on a loss, capped per UTC day. */
  applyPlayReduction(levelCleared: number, won: boolean, now: number): number {
    const day = Math.floor(now / 86400);
    if (day !== this.reductionDay) { this.reductionDay = day; this.reductionsToday = 0; }
    if (this.reductionsToday >= this.balance.meta.reductionsPerDay + this.vip.extraReductionsPerDay) return 0;
    this.reductionsToday++;
    const seconds = Math.trunc(this.balance.meta.reductionSecondsPerLevel * levelCleared * (won ? 1 : 0.5));
    for (const j of this.jobs) j.endAt = Math.max(now, j.endAt - seconds);
    return seconds;
  }

  /**
   * Cancels a running upgrade (§8): the builder is freed and `cancelRefundFraction` of the gold spent comes back.
   * Returns the refund, or null when no such job exists.
   */
  cancelUpgrade(jobId: number): number | null {
    const i = this.jobs.findIndex((j) => j.id === jobId);
    if (i < 0) return null;
    const job = this.jobs[i];
    const cost = this.costFor(job.toLevel);
    const f = this.balance.meta.cancelRefundFraction;
    const refund = Fix64.fromInt(cost).mul(f).floorToInt();
    this.jobs.splice(i, 1);
    this.addGold(refund);
    // What was queued on top of this job assumed its level: it goes too, paid back in full (meta/upgradeQueue.ts).
    const stacked = this.queue.find((q) => q.target === job.target && (job.target === UpgradeTarget.Base || q.towerIndex === job.towerIndex));
    if (stacked) cancelQueued(this, stacked.id);
    return refund;
  }

  addSpeedUp(minutes: number): void { if (Number.isInteger(minutes) && minutes > 0) this.speedUps.push(minutes); }

  /**
   * Applies one speed-up item (by inventory index) to a job (§9.3). A speed-up longer than the time left is trimmed:
   * the job completes and the unused remainder stays in the inventory as a smaller item. Returns seconds removed.
   */
  applySpeedUp(itemIndex: number, jobId: number, now: number): number | string {
    const job = this.jobs.find((j) => j.id === jobId);
    if (!job) return "no such job";
    if (itemIndex < 0 || itemIndex >= this.speedUps.length) return "no such item";
    const left = Math.max(0, job.endAt - now);
    if (left === 0) return "already complete";
    const minutes = this.speedUps[itemIndex];
    const seconds = Math.min(minutes * 60, left);
    job.endAt -= seconds;
    const remainderMinutes = Math.floor((minutes * 60 - seconds) / 60);
    if (remainderMinutes > 0) this.speedUps[itemIndex] = remainderMinutes; else this.speedUps.splice(itemIndex, 1);
    return seconds;
  }

  // ------------------------------------------------------------------ gear (§9.4, §9.6)

  /**
   * Rolls `count` items of a chest's rarity at its level into the inventory (deterministic from the seed). With
   * `seats`, each item rolls for a seat `seats.level`'s readiness plan still has free (§9.3 early gear, `seatedFit`),
   * preferring seats `seats.owned` can fill now; without, the slot is uniform.
   */
  openChestGear(chestLevel: number, rarity: Rarity, count: number, seed: bigint, seats: ChestSeats | null = null): GearItem[] {
    const rng = new Pcg32(seed, 137);
    const out: GearItem[] = [];
    const levelOf = (o: GearOwner) => (o.target === GearTarget.Base ? this.baseLevel : this.towerLevels[o.towerIndex]);
    for (let i = 0; i < count; i++) {
      const fit = seats !== null ? seatedFit(this.balance, this.gear, seats.level, seats.owned, levelOf, rng) : null;
      const it = generateGear(this.balance, this.nextGearId++, chestLevel, rarity, rng, fit);
      this.gear.push(it); out.push(it);
    }
    return out;
  }

  /**
   * Adds an item rolled elsewhere (a Trading Post or Black Market purchase, meta/market.ts) under a fresh id, unworn.
   * `bound`: it arrives bound, so the Trading Post never takes it (a Black Market item, bought with gems).
   */
  receiveGear(item: GearItem, opts: { bound?: boolean } = {}): GearItem {
    const it: GearItem = { ...item, id: this.nextGearId++, raised: 0, essenceSpent: 0, bound: opts.bound ?? false, locked: false, equippedTo: null };
    this.gear.push(it);
    return it;
  }

  findGear(id: number): GearItem | null { return this.gear.find((g) => g.id === id) ?? null; }

  /** The item in an owner's slot, if any. */
  equippedIn(owner: GearOwner, slot: number): GearItem | null {
    return this.gear.find((g) => g.slot === slot && g.equippedTo !== null && sameOwner(g.equippedTo, owner)) ?? null;
  }

  /**
   * Equips an item (§9.4): the slot must belong to the target kind, the item's level must not exceed the target's
   * level, and the slot must be free (unequip first). An item raised past the bind threshold binds on equip.
   */
  equipGear(itemId: number, owner: GearOwner): true | string {
    const fits = this.canWear(itemId, owner);
    if (fits !== true) return fits;
    const it = this.findGear(itemId)!;
    if (it.equippedTo !== null) return "already equipped";
    if (this.equippedIn(owner, it.slot) !== null) return "slot occupied";
    it.equippedTo = { target: owner.target, towerIndex: owner.target === GearTarget.Base ? 0 : owner.towerIndex };
    if (it.raised > this.balance.gear.bindAboveRaised) it.bound = true;
    return true;
  }

  /**
   * The standing half of equipGear's rules — slot kind, a real tower, the item level — whatever the item and the slot
   * hold now. An item already worn by this owner stays wearable there (a v1 save's +N can fold to a level above its
   * wearer's, docs/GEAR_V2.md §Migration).
   */
  canWear(itemId: number, owner: GearOwner): true | string {
    const it = this.findGear(itemId);
    if (!it) return "no such item";
    if (this.balance.gear.slots[it.slot].target !== owner.target) return "slot does not fit this target";
    if (owner.target === GearTarget.Tower && (owner.towerIndex < 0 || owner.towerIndex >= this.towerLevels.length || this.balance.towers[owner.towerIndex].isBase)) return "no such tower";
    if (it.equippedTo !== null && sameOwner(it.equippedTo, owner)) return true;
    if (it.level > this.ownerLevel(owner)) return "level too low";
    return true;
  }

  /** The persistent level of a gear owner (the fortress's or the tower type's). */
  ownerLevel(owner: GearOwner): number { return this.levelOf(owner.target === GearTarget.Base ? UpgradeTarget.Base : UpgradeTarget.Tower, owner.towerIndex); }

  unequipGear(itemId: number): true | string {
    const it = this.findGear(itemId);
    if (!it) return "no such item";
    if (it.equippedTo === null) return "not equipped";
    it.equippedTo = null;
    return true;
  }

  setGearLock(itemId: number, locked: boolean): true | string {
    const it = this.findGear(itemId);
    if (!it) return "no such item";
    it.locked = locked;
    return true;
  }

  /** Salvage (§9.6): any item, bound or not; Mythic needs `confirmMythic`; equipped items are unequipped first. Pays gold and essence. */
  salvageGear(itemId: number, confirmMythic = false): SalvageYield | string {
    const it = this.findGear(itemId);
    if (!it) return "no such item";
    if (it.rarity === Rarity.Mythic && !confirmMythic) return "mythic needs confirmation";
    const y = salvageYield(this.balance, it);
    this.gear.splice(this.gear.indexOf(it), 1);
    this.addGold(y.gold);
    this.forge.essence += y.essence;
    return y;
  }

  /** Bulk salvage (§9.6): rarity ≤ ceiling, not equipped, not locked, never Mythic. Returns the items' total. */
  bulkSalvage(maxRarity: Rarity): { count: number; gold: number; essence: number } {
    let gold = 0, essence = 0, count = 0;
    for (const it of [...this.gear]) {
      if (it.rarity > maxRarity || it.rarity === Rarity.Mythic || it.equippedTo !== null || it.locked) continue;
      const y = this.salvageGear(it.id) as SalvageYield;
      gold += y.gold; essence += y.essence; count++;
    }
    return { count, gold, essence };
  }

  /** A fresh item id (fusion keeps the keeper's; a test fixture may need one). */
  allocateGearId(): number { return this.nextGearId++; }

  /** Bonus the sim applies for one owner. */
  gearBonus(owner: GearOwner): GearBonus { return bonusFor(this.balance, this.gear, owner); }

  /**
   * Per tower archetype (base included at its own index): what the equipped gear, plus VIP's combat bonus
   * (meta/vipLevels.ts withVipCombat; nothing at VIP 0) and owned Gold Research (meta/research.ts withResearch; nothing
   * when none), contributes to a level. The ONE way VIP and research reach the sim.
   */
  gearBonuses(): GearBonus[] {
    const gear = this.balance.towers.map((t, i) => this.gearBonus(t.isBase ? { target: GearTarget.Base, towerIndex: 0 } : { target: GearTarget.Tower, towerIndex: i }));
    return withVipCombat(this.balance, withResearch(this.balance, gear, this.research), this.vip);
  }

  addGold(amount: number): void { this.gold += amount; }

  /** Weighted sum of levels plus the gear score (§7). */
  get powerScore(): number {
    let sum = this.baseLevel * this.balance.meta.powerBaseWeight;
    for (const l of this.towerLevels) sum += l * this.balance.meta.powerTowerWeight;
    return sum + gearScore(this.balance, this.gear);
  }

  toJson(): ProfileJson {
    return { towerLevels: [...this.towerLevels], baseLevel: this.baseLevel, gold: this.gold, buildersOwned: this.buildersOwned, jobs: this.jobs.map((j) => ({ ...j })), queue: this.queue.map((q) => ({ ...q })), nextJobId: this.nextJobId, reductionsToday: this.reductionsToday, reductionDay: this.reductionDay, speedUps: [...this.speedUps], gear: this.gear.map(gearToJson), nextGearId: this.nextGearId, forge: this.forge.toJson() };
  }

  static fromJson(balance: BalanceData, j: Partial<ProfileJson> | undefined): PlayerProfile {
    const p = new PlayerProfile(balance);
    if (!j) return p;
    if (Array.isArray(j.towerLevels)) j.towerLevels.forEach((l, i) => { if (i < p.towerLevels.length && Number.isInteger(l) && l >= 1) p.towerLevels[i] = l; });
    if (Number.isInteger(j.baseLevel) && j.baseLevel! >= 1) p.baseLevel = j.baseLevel!;
    // A pre-gold profile (`resources: {wood, iron, crystal}`) converts on read, like the save migration (meta/gold.ts).
    if (Number.isFinite(j.gold) && j.gold! >= 0) p.gold = Math.floor(j.gold!);
    else if (isLegacyResources(j.resources)) p.gold = goldFromLegacy(j.resources);
    if (Number.isInteger(j.buildersOwned) && j.buildersOwned! >= 1) p.buildersOwned = j.buildersOwned!;
    if (Array.isArray(j.jobs)) for (const job of j.jobs) p.jobs.push({ ...job });
    for (const q of parseQueued(j.queue)) p.queue.push(q);
    if (Number.isInteger(j.nextJobId)) p.nextJobId = j.nextJobId!;
    if (Number.isInteger(j.reductionsToday)) p.reductionsToday = j.reductionsToday!;
    if (Number.isInteger(j.reductionDay)) p.reductionDay = j.reductionDay!;
    if (Array.isArray(j.speedUps)) for (const m of j.speedUps) p.addSpeedUp(Number(m));
    if (Array.isArray(j.gear)) for (const g of j.gear) p.gear.push(gearFromJson(g));
    p.forge.load(j.forge);
    if (Number.isInteger(j.nextGearId)) p.nextGearId = j.nextGearId!;
    else p.nextGearId = p.gear.reduce((m, g) => Math.max(m, g.id + 1), 1);
    return p;
  }
}

export interface ProfileJson {
  towerLevels: number[];
  baseLevel: number;
  gold: number;
  /** Pre-gold saves only (read, never written): converted by `goldFromLegacy`. */
  resources?: unknown;
  buildersOwned: number;
  jobs: UpgradeJob[];
  /** Absent on saves from before the queue (D4). */
  queue?: QueuedUpgrade[];
  nextJobId: number;
  reductionsToday: number;
  reductionDay: number;
  speedUps?: number[];
  gear?: GearItemJson[];
  nextGearId?: number;
  /** Forge Essence, Sigils and the fusion tally (gear v2; absent before save schema 7). */
  forge?: ForgeJson;
}
